import React, { useContext, useEffect, useState } from "react";
import MaterialTable from "@material-table/core";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";

import {
  Avatar,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  RootRef,
  Snackbar,
  useMediaQuery,
  useTheme
} from "@material-ui/core";

import Typography from "@material-ui/core/Typography";
import { useDispatch, useSelector } from "react-redux";
import { Category } from "../../../models/Product";
import useStyles from "./styles";
import { AppState } from "../../../store";
import { Check, Clear, DragHandle } from "@material-ui/icons";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import { ProductCategoryEditor } from "./ProductCategoryEditor";
import {
  thunkFetchProductCategories,
  thunkFetchProductsV2
} from "../../../store/products/action";
import { thunkFetchCurrentTenantLanguages } from "../../../store/tenants/actions";
import { useLoadIfEmpty } from "../../../hooks/useLoadIfEmpty";
import * as ProductCategoryApi from "../../../api/productCategoryApi";
import { isOk } from "../../../result";
import { ConfirmDialog } from "../../../components/dialogs/ConfirmDialog";
import { CategorySelect } from "../../../components/selects/CategorySelect";
import { ProblemDetails } from "../../../models/Http";
import Alert from "@material-ui/lab/Alert";
import { Language } from "../../../models/Translation";
import { useI18n } from "../../../hooks/useI18n";
import { UserContext } from "../../../UserProvider";
import { isProductCampaignReadOnly } from "../../../models/User";

export const TenantProductCategories = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useI18n();
  const user = useContext(UserContext);
  const readOnly = isProductCampaignReadOnly(user);

  enum successType {
    none,
    add,
    update,
    delete,
    order
  }

  const categories =
    useSelector<AppState, ReadonlyArray<Category>>(
      s => s.storeClusterProducts.categories
    ) || [];

  const tenantLanguages =
    useSelector<AppState, ReadonlyArray<Language>>(
      s => s.currentTenant.languages
    ) || [];

  useLoadIfEmpty(!tenantLanguages.length, thunkFetchCurrentTenantLanguages);

  const [state, setState] = useState({
    displayEditCategory: false,
    displaySortCategories: false,
    displayDeleteCategory: false,
    deleteProducts: false,
    moveProductsToCategoryId: "",
    editCategory: {} as Category,
    categories: [] as Category[],
    reOrderCategories: [] as Category[],
    reOrderLoading: false,
    reload: false,
    error: {} as ProblemDetails,
    displaySuccess: successType.none
  });

  useEffect(() => {
    if (categories) {
      setState({
        ...state,
        categories: [...categories].sort(
          (a: any, b: any) => a.displayOrder - b.displayOrder
        ) as []
      });
    }
  }, [categories]);

  useEffect(() => {
    if (state.reload) {
      dispatch(thunkFetchProductCategories());
      dispatch(thunkFetchProductsV2());
      setState({ ...state, reload: false });
    }
  }, [state.reload]);

  const reorder = (list: any, startIndex: any, endIndex: any): any => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const getItemStyle = (isDragging: any, draggableStyle: any) => ({
    ...draggableStyle,
    borderBottom: "1px solid rgb(235,235,235)",
    ...(isDragging && {
      background: "rgb(235,235,235)"
    })
  });

  const onDragEnd = (result: any) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const items = reorder(
      state.reOrderCategories,
      result.source.index,
      result.destination.index
    );

    for (let i = 0; i < items.length; i++) {
      items[i].displayOrder = i + 1;
    }

    setState({
      ...state,
      reOrderCategories: items as []
    });
  };

  const getSuccessMessage = (): string => {
    switch (state.displaySuccess as successType) {
      case successType.add:
        return t("common.messages.categoryAdded");
      case successType.update:
        return t("common.messages.categoryUpdated");
      case successType.delete:
        return t("common.messages.categoryDeleted");
      case successType.order:
        return t("common.messages.categoryOrder");
      default:
        return "";
    }
  };

  return (
    <>
      <MaterialTable
        title=""
        isLoading={!categories}
        columns={[
          {
            title: "",
            field: "imageUrl",
            render: (rowData: any) => (
              <Avatar
                variant={"square"}
                imgProps={{ style: { objectFit: "contain" } }}
                src={rowData.imageUrl}
              >
                {rowData.translatedName}
              </Avatar>
            )
          },
          {
            title: t("common.categoryManagement.order"),
            field: "displayOrder"
          },
          {
            title: t("common.formFields.name"),
            field: "translatedName"
          },
          {
            title: t("common.formFields.isOrderable"),
            field: "isOrderable",
            render: (rowData: any) =>
              rowData.isOrderable ? (
                <Check
                  className={classes.check}
                  titleAccess={t("common.categoryManagement.availableToOrder")}
                />
              ) : (
                <Clear
                  className={classes.cross}
                  titleAccess={t(
                    "common.categoryManagement.notAvailableToOrder"
                  )}
                />
              )
          },
          {
            title: t("common.tableHeaders.productCount"),
            field: "productCount"
          }
        ]}
        onRowClick={
          readOnly
            ? undefined
            : (event, rowData: any) =>
                setState({
                  ...state,
                  displayEditCategory: true,
                  editCategory: rowData
                })
        }
        data={
          state.categories && state.categories.length
            ? state.categories.map(c => ({
                ...c,
                translatedName:
                  c.translation?.values[tenantLanguages[0].code] ?? c.name
              }))
            : []
        }
        options={{
          pageSize: 20,
          actionsColumnIndex: -1,
          actionsCellStyle: { whiteSpace: "nowrap" }
        }}
        actions={[
          {
            icon: "swap_vert",
            hidden: readOnly,
            iconProps: {
              style: { fontSize: "1.7rem" }
            },
            tooltip: t("common.categoryManagement.reorderCategories"),
            isFreeAction: true,
            onClick: () => {
              setState({
                ...state,
                displaySortCategories: true,
                reOrderCategories: [...state.categories]
              });
            }
          },
          {
            icon: "add_circle",
            hidden: readOnly,
            iconProps: {
              style: { color: "rgb(31, 132, 52)", fontSize: "2rem" }
            },
            tooltip: t("common.categoryManagement.addNewCategory"),
            isFreeAction: true,
            onClick: () => {
              setState({
                ...state,
                displayEditCategory: true,
                editCategory: {} as Category
              });
            }
          },
          {
            icon: "delete",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: t("common.actions.deleteCategory"),
            hidden: mobile || readOnly,
            onClick: (event, rowData: any) =>
              setState({
                ...state,
                displayDeleteCategory: true,
                editCategory: rowData
              })
          }
        ]}
        localization={{
          header: {
            actions: ""
          },
          toolbar: {
            searchPlaceholder: t("common.search"),
            searchTooltip: t("common.searchTooltip")
          },
          pagination: {
            labelRowsSelect: t("rows"),
            labelDisplayedRows: t("displayedRowsTemplate"),
            firstTooltip: t("first"),
            previousTooltip: t("prev"),
            nextTooltip: t("next"),
            lastTooltip: t("last")
          },
          body: {
            emptyDataSourceMessage: t("noRecords")
          }
        }}
      />

      <ConfirmDialog
        visible={state.displayDeleteCategory}
        titleText={t("common.actions.deleteCategory")}
        bodyText={`${t(
          "common.categoryManagement.deleteCategoryConfirmation"
        )} ${
          state.editCategory.productCount
            ? t("common.categoryManagement.selectWhatToDoWithProducts", {
                count: state.editCategory.productCount
              })
            : ""
        }`}
        confirmText={t("common.actions.delete")}
        onConfirm={async () => {
          const result = await ProductCategoryApi.deleteCategory(
            state.editCategory,
            state.deleteProducts,
            state.moveProductsToCategoryId
          );

          if (!isOk(result)) {
            setState({ ...state, error: result.details });
            return false;
          }

          setState({
            ...state,
            reload: true,
            displayDeleteCategory: false,
            editCategory: {} as Category,
            deleteProducts: false,
            moveProductsToCategoryId: "",
            error: {} as ProblemDetails,
            displaySuccess: successType.delete
          });
          return true;
        }}
        onCancel={() => {
          setState({
            ...state,
            displayDeleteCategory: false,
            editCategory: {} as Category,
            deleteProducts: false,
            moveProductsToCategoryId: "",
            error: {} as ProblemDetails
          });
        }}
      >
        {state.editCategory.productCount ? (
          <>
            <FormControlLabel
              style={{ marginBottom: 10 }}
              control={
                <Checkbox
                  checked={state.deleteProducts}
                  onChange={e =>
                    setState({
                      ...state,
                      deleteProducts: e.target.checked
                    })
                  }
                />
              }
              label={t("common.formFields.deleteRelatedProducts")}
            />
            <CategorySelect
              disabled={state.deleteProducts}
              label={t("common.categoryManagement.moveToCategory")}
              error={!state.editCategory?.id || state.editCategory?.id === ""}
              className={classes.field}
              value={
                state?.moveProductsToCategoryId !== ""
                  ? state?.moveProductsToCategoryId
                  : undefined
              }
              onSelected={category =>
                setState({
                  ...state,
                  moveProductsToCategoryId: category.id
                })
              }
            />
            {state.error?.title && (
              <Alert severity="error">
                {t("common.categoryManagement.errorDeletingCategory")}
              </Alert>
            )}
          </>
        ) : (
          <></>
        )}
      </ConfirmDialog>

      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={state.displayEditCategory}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setState({
                ...state,
                displayEditCategory: false,
                editCategory: {} as Category
              });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <ProductCategoryEditor
            category={state.editCategory}
            onSave={() =>
              setState({
                ...state,
                reload: true,
                displayEditCategory: false,
                editCategory: {} as Category,
                displaySuccess: state.editCategory?.name
                  ? successType.update
                  : successType.add
              })
            }
          />
        </DialogContent>
      </Dialog>

      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={state.displaySortCategories}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <Typography variant="h6">
            {t("common.messages.categoryOrder")}
          </Typography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setState({
                ...state,
                displaySortCategories: false
              });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <div>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="droppable">
                {(provided: any, snapshot: any) => (
                  <RootRef rootRef={provided.innerRef}>
                    <List dense ref={provided.innerRef}>
                      {state.reOrderCategories.map((item: Category, index) => (
                        <Draggable
                          key={item.id}
                          draggableId={item.id}
                          index={index}
                        >
                          {(provided: any, snapshot: any) => (
                            <ListItem
                              ref={provided.innerRef}
                              ContainerComponent="li"
                              ContainerProps={{ ref: provided.innerRef }}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={getItemStyle(
                                snapshot.isDragging,
                                provided.draggableProps.style
                              )}
                            >
                              <ListItemIcon>
                                <DragHandle />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  item.translation?.values[
                                    tenantLanguages[0].code
                                  ] ?? item.name
                                }
                              />
                            </ListItem>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </List>
                  </RootRef>
                )}
              </Droppable>
            </DragDropContext>
          </div>
          <Button
            style={{ width: 300, margin: "auto", display: "flex" }}
            variant="contained"
            color="primary"
            disabled={state.reOrderLoading}
            onClick={async () => {
              setState({ ...state, reOrderLoading: true });
              const result = await ProductCategoryApi.updateCategoryOrder(
                state.reOrderCategories
              );

              if (!isOk(result)) {
                setState({ ...state, error: result.details });
                return;
              }

              setState({
                ...state,
                reload: true,
                displaySortCategories: false,
                reOrderLoading: false,
                displaySuccess: successType.order,
                error: {} as ProblemDetails
              });
            }}
          >
            {t("common.categoryManagement.saveCategoryOrder")}{" "}
            {state.reOrderLoading && (
              <CircularProgress size={20} style={{ marginLeft: 20 }} />
            )}
          </Button>
          {state.error?.title && (
            <Alert severity="error">
              {t("common.categoryManagement.errorOrderingCategories")}
            </Alert>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={state.displaySuccess !== successType.none}
        autoHideDuration={2000}
        onClose={() => {
          setState({ ...state, displaySuccess: successType.none });
        }}
      >
        <Alert
          onClose={() =>
            setState({ ...state, displaySuccess: successType.none })
          }
          severity="success"
        >
          {getSuccessMessage()}
        </Alert>
      </Snackbar>
    </>
  );
};
